/**
 * Helper functions for action-related operations
 */

/**
 * Format file name(s) for display in the UI
 * 
 * @param fileName - Single file name or array of file names
 * @returns Formatted string for display
 * 
 * @example
 * formatFileNameForDisplay('document.pdf') // 'document.pdf'
 * formatFileNameForDisplay(['doc1.pdf']) // 'doc1.pdf'
 * formatFileNameForDisplay(['doc1.pdf', 'doc2.pdf']) // '2 files selected'
 * formatFileNameForDisplay([]) // 'No files selected'
 */
export function formatFileNameForDisplay(fileName: string | string[]): string {
  if (Array.isArray(fileName)) {
    if (fileName.length === 0) return 'No files selected';
    if (fileName.length === 1) return fileName[0];
    return `${fileName.length} files selected`;
  }
  return fileName;
}

/**
 * Get a short display version of file name(s) for toasts and notifications
 * 
 * @param fileName - Single file name or array of file names
 * @returns Short formatted string for display
 * 
 * @example
 * getFileDisplayShort('document.pdf') // 'document.pdf'
 * getFileDisplayShort(['doc1.pdf']) // 'doc1.pdf'
 * getFileDisplayShort(['doc1.pdf', 'doc2.pdf']) // '2 files'
 */
export function getFileDisplayShort(fileName: string | string[]): string {
  if (Array.isArray(fileName)) {
    if (fileName.length === 0) return 'no files';
    if (fileName.length === 1) return fileName[0];
    return `${fileName.length} files`;
  }
  return fileName;
}

/**
 * Get detailed file list for tooltips or expanded views
 * 
 * @param fileName - Single file name or array of file names
 * @param maxDisplay - Maximum number of files to show before truncating
 * @returns Formatted string with file list
 * 
 * @example
 * getFileListDetailed(['doc1.pdf', 'doc2.pdf', 'doc3.pdf'], 2)
 * // 'doc1.pdf, doc2.pdf, and 1 more'
 */
export function getFileListDetailed(
  fileName: string | string[], 
  maxDisplay: number = 3
): string {
  if (Array.isArray(fileName)) {
    if (fileName.length === 0) return 'No files';
    if (fileName.length <= maxDisplay) {
      return fileName.join(', ');
    }
    const displayed = fileName.slice(0, maxDisplay).join(', ');
    const remaining = fileName.length - maxDisplay;
    return `${displayed}, and ${remaining} more`;
  }
  return fileName;
}
